import React, { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button, IconButton, Box } from "@mui/material";
import { Add, Edit, InfoOutlined } from "@mui/icons-material";
import AppTable from "components/Shared/AppTable";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import { useTablePagination, useAppMeta, useAuth } from "hooks";
import { BaseTabs } from "components/Shared/BaseTabs";
import HighlightedText from "components/Shared/HighlightedText";
import { useLazyListLocalTransportRuleQuery } from "store/services/LocalTransportRuleApi";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import LocalTransportRuleFilter from "./LocalTransportRuleFilter";
import { useCustomParams } from "hooks/useCustomParams";


const LocalTransportRuleListScreen = () => {
  const { isAccountAdmin } = useAuth();
  const { params } = useCustomParams();

  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();

  const [searchParams] = useSearchParams();

  const activeTab = searchParams.get("tab") || "Active";
  const keyword = searchParams.get("search") || "";
  const branch = params.branch || "";
  const toBranch = params.toBranch || "";

  const [fetchRules, { data: res, isLoading, isFetching, error }] =
    useLazyListLocalTransportRuleQuery();

  const columns = useMemo(
    () => [
      {
        name: "From Branch",
        cell: (row) => {
          const branches = Array.isArray(row.branch)
            ? row.branch
            : row.branch
              ? [row.branch]
              : [];

          if (branches.length === 0) return "N/A";

          const branchNames = branches
            .map((b) => b?.name || "")
            .filter(Boolean);
          const displayText = branchNames.join(", ");

          return (
            <Box
              sx={{
                maxWidth: 180,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                cursor: branchNames.length > 1 ? "pointer" : "default",
              }}
              title={branchNames.length > 1 ? branchNames.join(", ") : ""}
            >
              {displayText || "N/A"}
            </Box>
          );
        },
        sortable: false,
      },
      {
        name: "To Branch",
        cell: (row) => {
          const branches = Array.isArray(row.toBranch)
            ? row.toBranch
            : row.toBranch
              ? [row.toBranch]
              : [];

          if (branches.length === 0) return "N/A";

          const branchNames = branches
            .map((b) => b?.name || "")
            .filter(Boolean);
          const displayText = branchNames.join(", ");

          return (
            <Box
              sx={{
                maxWidth: 180,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                cursor: branchNames.length > 1 ? "pointer" : "default",
              }}
              title={branchNames.length > 1 ? branchNames.join(", ") : ""}
            >
              {displayText || "N/A"}
            </Box>
          );
        },
        sortable: false,
      },
      {
        name: "Price per Sack",
        selector: (row) => row?.pricePerSack || "N/A",
        cell: (row) => row?.pricePerSack || "N/A",
      },
      {
        name: "Mode",
        selector: (row) => row.transportationMode?.name || "N/A",
        cell: (row) => (
          <HighlightedText
            text={row.transportationMode?.name || "N/A"}
            highlight={keyword}
          />
        ),
      },
      {
        name: "Max Dist (km)",
        selector: (row) => row.maxDistanceKm,
      },
      {
        name: "Max Cost",
        selector: (row) => row.maxCost || "N/A",
      },
      {
        name: "Created At",
        selector: (row) => <AppTableDateTime date={row?.createdAt} />,
      },
      {
        name: "Actions",
        cell: (row) => (
          <>
            <Link to={`/app/crm/local-transport-rule/${row._id}/detail`}>
              <IconButton size="small" color="primary">
                <InfoOutlined />
              </IconButton>
            </Link>
            {isAccountAdmin && (
              <Link to={`/app/crm/local-transport-rule/${row._id}/edit`}>
                <IconButton size="small">
                  <Edit />
                </IconButton>
              </Link>)}
          </>
        ),
        ignoreRowClick: true,
        right: true,
      },
    ],
    [keyword, isAccountAdmin]
  );

  useEffect(() => {
    fetchRules({
      active: activeTab === "Active",
      page: currentPage,
      limit: perPage,
      keyword,
      branch,
      toBranch,
    });
  }, [activeTab, currentPage, perPage, keyword, branch, toBranch, fetchRules]);

  useAppMeta(`${activeTab} Local Transport Rules`);

  return (
    <BreadcrumbLayout
      breadcrumbs={[{ title: "Local Transport Rules" }]}
      headerActions={<PageActions />}
    >
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <BaseTabs
          tabs={[
            { label: "Active", value: "Active" },
            { label: "Inactive", value: "Inactive" },
          ]}
          defaultValue={activeTab}
        />

        {/* Filter Component */}
        <LocalTransportRuleFilter />

        <AppTable
          columns={columns}
          data={res?.data ?? []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationTotalRows={res?.totalItems ?? 0}
          paginationDefaultPage={currentPage}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default LocalTransportRuleListScreen;

const PageActions = () => {
  const { isAccountAdmin } = useAuth();

  return (
    <Link to="/app/crm/local-transport-rule/create">
      {isAccountAdmin && (
        <Button startIcon={<Add />} variant="contained">
          New Rule
        </Button>
      )}
    </Link>
  );
};